import React from 'react';
import { Watch, Target, Trophy } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { appFeatures } from '../data/mock';

const iconMap = {
  Watch: Watch,
  Target: Target,
  Trophy: Trophy
};

const AppShowcase = () => {
  const { language } = useLanguage();

  return (
    <section className="py-24 bg-gradient-to-b from-[#1a3334] to-[#0d1f1f] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-400 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-5xl md:text-6xl font-bold text-white leading-tight">
            {language === 'en' ? 'Live' : 'Vive'}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
              {language === 'en' ? 'Healthier' : 'Más Saludable'}
            </span>
          </h2>
        </div>

        {/* App Mockups and Features */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Phone Mockups */}
          <div className="relative flex justify-center items-center">
            {/* Main Phone */}
            <div className="relative z-20 transform hover:scale-105 transition-transform duration-500">
              <div className="w-[280px] h-[580px] bg-gradient-to-br from-gray-800 to-gray-900 rounded-[3rem] p-3 shadow-2xl border-4 border-gray-700">
                <div className="w-full h-full bg-[#0d1f1f] rounded-[2.5rem] overflow-hidden relative">
                  {/* Status Bar */}
                  <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-black/50 to-transparent flex items-center justify-between px-8 text-white text-xs">
                    <span>13:08</span>
                    <div className="flex space-x-1">
                      <div className="w-4 h-4 rounded-full bg-white"></div>
                    </div>
                  </div>

                  {/* App Content */}
                  <div className="pt-16 px-6 space-y-6">
                    {/* Activity Ring */}
                    <div className="flex justify-center">
                      <div className="relative w-40 h-40">
                        <svg className="transform -rotate-90" viewBox="0 0 120 120">
                          <circle cx="60" cy="60" r="54" fill="none" stroke="#1a3334" strokeWidth="8" />
                          <circle cx="60" cy="60" r="54" fill="none" stroke="#10b981" strokeWidth="8" strokeDasharray="339.3" strokeDashoffset="85" strokeLinecap="round" />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-emerald-400 text-2xl font-bold">Active</span>
                          <span className="text-gray-400 text-sm">75% Goal</span>
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="bg-gray-800/50 rounded-xl p-3">
                        <div className="text-emerald-400 font-bold text-lg">2h 38m</div>
                        <div className="text-gray-500 text-xs">Fitness</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-xl p-3">
                        <div className="text-emerald-400 font-bold text-lg">8895</div>
                        <div className="text-gray-500 text-xs">Steps</div>
                      </div>
                      <div className="bg-gray-800/50 rounded-xl p-3">
                        <div className="text-emerald-400 font-bold text-lg">2,679</div>
                        <div className="text-gray-500 text-xs">Kcal</div>
                      </div>
                    </div>

                    {/* Categories */}
                    <div className="space-y-3">
                      {['Nutrition', 'Well-being'].map((category, idx) => (
                        <div key={idx} className="bg-gray-800/50 rounded-xl p-4 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                              <div className="w-5 h-5 rounded-full bg-emerald-400"></div>
                            </div>
                            <span className="text-white font-medium">{category}</span>
                          </div>
                          <div className="text-gray-400">›</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary Phone (Background) */}
            <div className="absolute left-0 -z-10 opacity-50 blur-sm transform scale-90">
              <div className="w-[280px] h-[580px] bg-gradient-to-br from-gray-700 to-gray-800 rounded-[3rem] p-3">
                <div className="w-full h-full bg-[#1a3334] rounded-[2.5rem]"></div>
              </div>
            </div>
          </div>

          {/* Features List */}
          <div className="space-y-8">
            {appFeatures.map((feature, index) => {
              const Icon = iconMap[feature.icon];
              return (
                <div
                  key={feature.id}
                  className="flex items-start space-x-6 group cursor-pointer"
                >
                  <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center group-hover:bg-emerald-500 group-hover:scale-110 transition-all duration-300">
                    <Icon className="w-8 h-8 text-emerald-400 group-hover:text-white transition-colors" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-2xl font-bold text-white group-hover:text-emerald-400 transition-colors">
                      {language === 'en' ? feature.title_en : feature.title_es}
                    </h3>
                    <p className="text-gray-400 text-lg leading-relaxed">
                      {language === 'en' ? feature.description_en : feature.description_es}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppShowcase;